import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { 
  Link2, 
  Copy, 
  Check, 
  Lock, 
  Globe, 
  Mail, 
  Clock,
  Camera,
  Radio,
  QrCode
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShareModal = ({ open, onClose, dashboardType, title }) => {
  const { selectedYear, periodType, selectedPeriod } = useApp();
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    title: title || `${dashboardType} Dashboard`,
    accessType: 'public',
    password: '',
    allowedEmails: '',
    allowedDomains: 'playershealth.com',
    dataType: 'live',
    expiry: '7d'
  });

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        dashboard_type: dashboardType,
        title: formData.title,
        year: selectedYear,
        period_type: periodType,
        period_start: selectedPeriod?.start,
        period_end: selectedPeriod?.end,
        access_type: formData.accessType,
        password: formData.accessType === 'password' ? formData.password : null,
        allowed_emails: formData.accessType === 'email_restricted' 
          ? formData.allowedEmails.split(',').map(e => e.trim()).filter(Boolean)
          : [],
        allowed_domains: formData.accessType === 'email_restricted'
          ? formData.allowedDomains.split(',').map(d => d.trim()).filter(Boolean)
          : [],
        data_type: formData.dataType,
        expiry: formData.expiry
      };

      const response = await axios.post(`${API_URL}/api/shares`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedLink(response.data);
      toast.success('Share link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  };

  const getShareUrl = () => {
    if (!createdLink) return '';
    return `${window.location.origin}/shared/${createdLink.id}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleClose = () => {
    setCreatedLink(null);
    setFormData({
      title: title || `${dashboardType} Dashboard`,
      accessType: 'public',
      password: '',
      allowedEmails: '',
      allowedDomains: 'playershealth.com',
      dataType: 'live',
      expiry: '7d'
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#2563EB]" />
            Share Dashboard
          </DialogTitle>
          <DialogDescription>
            Create a shareable link for this dashboard view
          </DialogDescription>
        </DialogHeader>

        {!createdLink ? (
          <div className="space-y-4 py-2">
            {/* Title */}
            <div className="space-y-2">
              <Label>Link Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Q4 2025 Board Review"
              />
            </div>

            {/* Access Type */}
            <div className="space-y-2">
              <Label>Access Control</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setFormData({ ...formData, accessType: 'public' })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.accessType === 'public'
                      ? 'border-[#2563EB] bg-[#EFF6FF]'
                      : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  <Globe className={`w-5 h-5 mb-1 ${formData.accessType === 'public' ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                  <p className="text-sm font-medium text-[#111827]">Public</p>
                  <p className="text-xs text-[#6B7280]">Anyone with link</p>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, accessType: 'password' })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.accessType === 'password'
                      ? 'border-[#2563EB] bg-[#EFF6FF]'
                      : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  <Lock className={`w-5 h-5 mb-1 ${formData.accessType === 'password' ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                  <p className="text-sm font-medium text-[#111827]">Password</p>
                  <p className="text-xs text-[#6B7280]">Requires password</p>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, accessType: 'email_restricted' })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.accessType === 'email_restricted'
                      ? 'border-[#2563EB] bg-[#EFF6FF]'
                      : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  <Mail className={`w-5 h-5 mb-1 ${formData.accessType === 'email_restricted' ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                  <p className="text-sm font-medium text-[#111827]">Email</p>
                  <p className="text-xs text-[#6B7280]">Restricted domains</p>
                </button>
              </div>
            </div>

            {/* Password input */}
            {formData.accessType === 'password' && (
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter a password"
                />
              </div>
            )}

            {/* Email restrictions */}
            {formData.accessType === 'email_restricted' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Allowed Domains</Label>
                  <Input
                    value={formData.allowedDomains}
                    onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
                    placeholder="playershealth.com, partner.com"
                  />
                  <p className="text-xs text-[#6B7280]">Comma-separated list of email domains</p>
                </div>
                <div className="space-y-2">
                  <Label>Additional Emails (optional)</Label>
                  <Textarea
                    value={formData.allowedEmails}
                    onChange={(e) => setFormData({ ...formData, allowedEmails: e.target.value })}
                    placeholder="investor@example.com, board@example.com"
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Data Type */}
            <div className="space-y-2">
              <Label>Data Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setFormData({ ...formData, dataType: 'live' })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.dataType === 'live'
                      ? 'border-[#2563EB] bg-[#EFF6FF]'
                      : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  <Radio className={`w-5 h-5 mb-1 ${formData.dataType === 'live' ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                  <p className="text-sm font-medium text-[#111827]">Live Data</p>
                  <p className="text-xs text-[#6B7280]">Always shows current data</p>
                </button>
                <button
                  onClick={() => setFormData({ ...formData, dataType: 'snapshot' })}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    formData.dataType === 'snapshot'
                      ? 'border-[#2563EB] bg-[#EFF6FF]'
                      : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                  }`}
                >
                  <Camera className={`w-5 h-5 mb-1 ${formData.dataType === 'snapshot' ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} />
                  <p className="text-sm font-medium text-[#111827]">Snapshot</p>
                  <p className="text-xs text-[#6B7280]">Frozen at time of share</p>
                </button>
              </div>
            </div>

            {/* Expiry */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#6B7280]" />
                Link Expiry
              </Label>
              <Select value={formData.expiry} onValueChange={(val) => setFormData({ ...formData, expiry: val })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24 hours</SelectItem>
                  <SelectItem value="7d">7 days</SelectItem>
                  <SelectItem value="30d">30 days</SelectItem>
                  <SelectItem value="never">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Current view info */}
            <div className="bg-[#F9FAFB] rounded-lg p-3 text-sm">
              <p className="text-[#6B7280] mb-1">Sharing current view:</p>
              <p className="text-[#111827] font-medium">
                {selectedYear} • {periodType.charAt(0).toUpperCase() + periodType.slice(1)}
                {selectedPeriod && ` • ${selectedPeriod.shortLabel}`}
              </p>
            </div>

            {/* Create button */}
            <Button 
              onClick={handleCreate} 
              disabled={isCreating || (formData.accessType === 'password' && !formData.password)}
              className="w-full bg-[#2563EB] hover:bg-[#1D4ED8]"
            >
              {isCreating ? 'Creating...' : 'Create Share Link'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Success state */}
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-[#ECFDF5] rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-[#059669]" />
              </div>
              <h3 className="text-lg font-semibold text-[#111827]">Link Created!</h3>
              <p className="text-sm text-[#6B7280]">{createdLink.title}</p>
            </div>

            {/* Link display */}
            <div className="bg-[#F9FAFB] rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Input
                  value={getShareUrl()}
                  readOnly
                  className="text-sm font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyToClipboard}
                  className="flex-shrink-0"
                >
                  {copied ? <Check className="w-4 h-4 text-[#059669]" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Link info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-[#F9FAFB] rounded-lg p-3">
                <p className="text-[#6B7280]">Access</p>
                <p className="font-medium text-[#111827] capitalize">{createdLink.access_type.replace('_', ' ')}</p>
              </div>
              <div className="bg-[#F9FAFB] rounded-lg p-3">
                <p className="text-[#6B7280]">Data</p>
                <p className="font-medium text-[#111827] capitalize">{createdLink.data_type}</p>
              </div>
              <div className="bg-[#F9FAFB] rounded-lg p-3 col-span-2">
                <p className="text-[#6B7280]">Expires</p>
                <p className="font-medium text-[#111827]">
                  {createdLink.expires_at 
                    ? new Date(createdLink.expires_at).toLocaleDateString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })
                    : 'Never'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={handleClose}>
                Done
              </Button>
              <Button 
                className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]"
                onClick={() => setCreatedLink(null)}
              >
                Create Another
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
